import { defaultSave, SAVE_VERSION, SaveSchema, type Save } from './schema';

/**
 * Upgrades a stored save of any past version to the current one. Old saves must never break:
 * add a step here for every version bump, and a test that migrates a real old blob.
 *
 *   v0 (no save) → v1: a fresh save.
 *   v1 → v2: the Leaf Blower was replaced by the String Trimmer (owners get the trimmer).
 */
const MIGRATIONS: Record<number, (raw: Record<string, unknown>) => Record<string, unknown>> = {
  1: (v1) => {
    const swap = (id: unknown) => (id === 'leafBlower' ? 'stringTrimmer' : id);
    const tools = (v1.tools ?? {}) as {
      owned?: unknown[];
      equipped?: unknown[];
      levels?: Record<string, unknown>;
    };
    const levels = { ...tools.levels };
    if ('leafBlower' in levels) {
      levels.stringTrimmer = levels.leafBlower;
      delete levels.leafBlower;
    }
    return {
      ...v1,
      version: 2,
      tools: {
        ...tools,
        owned: (tools.owned ?? []).map(swap),
        equipped: (tools.equipped ?? []).map(swap),
        levels,
      },
    };
  },
};

export type LoadResult =
  | { status: 'fresh'; save: Save }
  | { status: 'loaded'; save: Save; migratedFrom?: number }
  /** Unreadable data: start over, but the caller should keep the bad blob as a backup. */
  | { status: 'corrupt'; save: Save; error: string };

export function loadSave(json: string | null): LoadResult {
  if (json === null || json === '') return { status: 'fresh', save: defaultSave() };
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch (e) {
    return { status: 'corrupt', save: defaultSave(), error: `invalid JSON: ${String(e)}` };
  }
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return { status: 'corrupt', save: defaultSave(), error: 'not an object' };
  }
  let doc = raw as Record<string, unknown>;
  const from = typeof doc.version === 'number' ? doc.version : 0;
  if (from > SAVE_VERSION) {
    return { status: 'corrupt', save: defaultSave(), error: `from the future (v${from})` };
  }
  for (let v = from; v < SAVE_VERSION; v++) {
    const step = MIGRATIONS[v];
    if (!step) return { status: 'corrupt', save: defaultSave(), error: `no migration from v${v}` };
    doc = step(doc);
  }
  const parsed = SaveSchema.safeParse(doc);
  if (!parsed.success) {
    return { status: 'corrupt', save: defaultSave(), error: parsed.error.message };
  }
  return from === SAVE_VERSION
    ? { status: 'loaded', save: parsed.data }
    : { status: 'loaded', save: parsed.data, migratedFrom: from };
}

export function serializeSave(save: Save): string {
  return JSON.stringify(save);
}
